import { useMemo } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'
import { useAuthentication } from '@repo/unicity'
import { countryState } from '@/core/store/country.store'
import { userStatusAtom, clearUserStatus } from '@/core/auth/user-status.store'
import { feelgreatSettingsAtom } from '@/core/feelgreat/feelgreat.store'
import { useBuyNowText } from '@/core/feelgreat/feelgreat.hooks'
import { localize } from '@/core/utils'
import AccountCard from './user-layout/AccountCard'
import Breadcrumb from './user-layout/Breadcrumb'
import { MOBILE_QUERY, PAGE_BG } from './user-layout/constants'
import { MENU_ITEMS, filterMenu, findActiveMenu } from './user-layout/menu-items'
import { useCustomerDisplay } from './user-layout/hooks/use-customer-display'
import { analytics } from '@/core/analytics/analytics'

type FGMainBanner = {
    title?: { english?: string; native?: string }
    description?: { english?: string; native?: string }
    isEnable?: boolean
}

const FULL_PAGE_PATHS = ['/user/subscriptions/create-order', '/user/subscriptions/create-order-success']

const UserLayout = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const isMobile = useMediaQuery(MOBILE_QUERY)
    const { logout, loading: authLoading } = useAuthentication({ enablePersist: true })
    const setUserStatus = useSetAtom(userStatusAtom)
    const country = useAtomValue(countryState)
    const userStatus = useAtomValue(userStatusAtom)
    const settings = useAtomValue(feelgreatSettingsAtom) as Record<string, unknown> | null

    const { customerName, profilePicUrl, baId, isLoading: isCustomerDisplayLoading } = useCustomerDisplay()
    const buyNowText = useBuyNowText()

    const mainBanner = (settings?.mainBanner ?? null) as FGMainBanner | null
    const bannerTitle = localize(mainBanner?.title)
    const bannerDescription = localize(mainBanner?.description)
    const banner = mainBanner?.isEnable ? { title: bannerTitle, description: bannerDescription, buyNowText } : null

    const country2 = country.shorter?.toUpperCase() || ''
    const filteredMenu = useMemo(() => filterMenu(MENU_ITEMS, { country2, userStatus }), [country2, userStatus])
    const activeMenu = findActiveMenu(filteredMenu, location.pathname) ?? findActiveMenu(MENU_ITEMS, location.pathname)

    const isFullPage = FULL_PAGE_PATHS.includes(location.pathname)

    const showLeftCard = !isMobile || !activeMenu
    const showRightContent = !isMobile || !!activeMenu
    const isLeftCardFullscreen = isMobile && !activeMenu

    const handleLogout = () => {
        analytics.track('logout')
        // Drop user_id here, not in the timeout: everything tracked from now on is a guest.
        analytics.reset()
        navigate('/', { replace: true })
        // Clear auth after the route changes so AccountCard does not flicker empty data.
        window.setTimeout(() => {
            logout()
            setUserStatus(null)
            clearUserStatus()
        }, 0)
    }

    if (isFullPage) {
        return (
            <Box
                sx={{
                    bgcolor: PAGE_BG,
                    minHeight: 'calc(100vh - 75px)',
                    display: 'flex',
                    flexDirection: 'column',
                    px: { xs: '20px', md: '30px' },
                    py: { xs: '30px', md: '40px' }
                }}
                data-testid="user-box-full-page">
                <Box sx={{ flex: 1, width: '100%', maxWidth: 900, mx: 'auto' }} data-testid="user-box-full-page-inner">
                    <Outlet data-testid="user-outlet" />
                </Box>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                bgcolor: PAGE_BG,
                minHeight: 'calc(100vh - 75px)',
                pt: isLeftCardFullscreen ? 0 : { xs: '30px', md: '30px' },
                pb: isLeftCardFullscreen ? 0 : { xs: '30px', md: '40px' },
                px: isLeftCardFullscreen ? 0 : { xs: '20px', md: '30px' },
                display: 'flex',
                flexDirection: 'column'
            }}
            data-testid="user-box-1">
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    maxWidth: 1300,
                    mx: 'auto'
                }}
                data-testid="user-box-2">
                {activeMenu && <Breadcrumb activeMenu={activeMenu} data-testid="user-breadcrumb" />}

                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={isMobile ? 2 : 3}
                    alignItems={isMobile ? 'stretch' : 'flex-start'}
                    sx={{ flex: 1 }}
                    data-testid="user-stack">
                    {showLeftCard && (
                        <AccountCard
                            isMobile={isMobile}
                            isFullscreen={isLeftCardFullscreen}
                            customerName={customerName}
                            profilePicUrl={profilePicUrl}
                            baId={baId}
                            isProfileLoading={authLoading || isCustomerDisplayLoading}
                            banner={banner}
                            menuItems={filteredMenu}
                            onLogout={handleLogout}
                            data-testid="user-account-card"
                        />
                    )}

                    {showRightContent && (
                        <Stack flex={1} minWidth={0} spacing={2} width="100%" data-testid="user-stack-outlet">
                            <Outlet data-testid="user-outlet" />
                        </Stack>
                    )}
                </Stack>
            </Box>
        </Box>
    )
}

export default UserLayout
